"use client";

import {
  BuilderBlockProps,
  CallbackReturnType,
  ComponentWithAnalyticsGlobalField,
} from "@/types";
import { PropsWithChildren, useState, useMemo, FC, useEffect } from "react";
import {
  type OutputType,
  type InteractiveTableCategory,
  MaturityModel,
  MaturityModelProvider,
  type Challenge,
} from "@/components/ITSMMaturityModel";

type MaturityModelBlockProps = {
  colTitle: string;
  rowTitle: string;
  colHeader: {
    hasCards?: boolean;
    content?: string;
    header: InteractiveTableCategory;
  }[];
  rowHeader: InteractiveTableCategory[];
  tableCells?: Array<{
    rowCells: Array<{
      header: string;
      colIndex: number;
      description: string;
      horizontalCategoryGroup: string;
      verticalCategoryGroup: string;
      challengeKeys?: Array<{ key: string }>;
      selection?: {
        header: string;
        ctaText: string;
        analytics?: ComponentWithAnalyticsGlobalField;
        options: Array<{ icon: "checkmark" | "star"; description: string }>;
      };
    }>;
  }>;
  controlsTitle?: string;
  controlsOptions?: Array<{ icon: "checkmark" | "star"; description: string }>;
  clearButtonText?: string;
  dld: string;
  dlt: string;
  clearModalConfig?: {
    headline: string;
    description: string;
    backButtonText: string;
    backButtonAnalytics?: ComponentWithAnalyticsGlobalField;
    startNewButtonText: string;
    startNewButtonAnalytics?: ComponentWithAnalyticsGlobalField;
  };
  builderState?: {
    state: {
      selectedChallenge: Challenge | null;
      [key: string]: unknown;
    };
  };
  selectedChallenge?: Challenge | null;
  onChallengeChange?: (
    value: Challenge | null,
    elem: HTMLDivElement | null,
  ) => CallbackReturnType;
} & BuilderBlockProps;

export const MaturityModelBlock: FC<
  PropsWithChildren<MaturityModelBlockProps>
> = (props) => {
  const [hasSelectedItems, setHasSelectedItems] = useState(false);
  const [resetCounter, setResetCounter] = useState(0);
  const [localChallenge, setLocalChallenge] = useState<Challenge | null>(
    props.selectedChallenge ||
      props.builderState?.state?.selectedChallenge ||
      null,
  );
  useEffect(() => {
    const incomingValue =
      props.selectedChallenge ||
      props.builderState?.state?.selectedChallenge ||
      null;
    setLocalChallenge(incomingValue);
  }, [props.selectedChallenge, props.builderState?.state?.selectedChallenge]);

  const currentSelection =
    props.selectedChallenge ||
    props.builderState?.state?.selectedChallenge ||
    null;

  const totalColumns = useMemo(
    () => props.colHeader?.length || 5,
    [props.colHeader?.length],
  );

  const tableCells = useMemo(() => {
    if (!props.tableCells) return [];

    return props.tableCells.map((rowData) => {
      const rowArray: (OutputType | null)[] = new Array(totalColumns).fill(
        null,
      );
      const cells = rowData?.rowCells || [];

      cells.forEach((cell) => {
        if (!cell) return;

        if (typeof cell.colIndex === "number" && cell.colIndex < totalColumns) {
          const cellChallengeKeys =
            cell.challengeKeys?.map((item) => item.key) || [];

          const hasValidSelection = currentSelection && currentSelection.key;

          const isActive = hasValidSelection
            ? cellChallengeKeys.includes(currentSelection.key)
            : true;

          rowArray[cell.colIndex] = {
            header: cell.header,
            description: cell.description,
            horizontalCategory: {
              categoryGroup: cell.horizontalCategoryGroup || "Capability",
            },
            verticalCategory: {
              categoryGroup: cell.verticalCategoryGroup || "Maturity Level",
            },
            selection: cell.selection || {},
            tableChallenges: cellChallengeKeys,
            isActive: isActive,
          } as OutputType;
        }
      });
      return rowArray;
    });
  }, [props.tableCells, totalColumns, currentSelection]);

  const colHeader = props.colHeader?.map((item) => ({
    hasCards: item.hasCards ?? false,
    content: item.content,
    header: {
      value: item.header?.value,
      categoryKey: item.header.categoryKey,
      content: item.header?.content,
      analytics: item.header?.analytics,
    },
  }));

  const rowHeader = props.rowHeader?.map((item) => ({
    value: item.value,
    categoryKey: item.categoryKey,
    content: item.content,
    analytics: item.analytics,
  }));

  const handleSetSelectedChallenge = async (challenge: Challenge | null) => {
    setLocalChallenge(challenge);
    if (props.onChallengeChange) {
      await props.onChallengeChange(challenge, null);
    }
  };

  const handleClearModel = () => {
    setResetCounter(resetCounter + 1);
    setHasSelectedItems(false);
    handleSetSelectedChallenge(null);
  };

  const handleSelectionValidChange = (hasValidSelection: boolean) => {
    setHasSelectedItems(hasValidSelection);
  };

  return (
    <MaturityModelProvider
      hasSelectedItems={hasSelectedItems}
      hasSelectedChallenge={!!localChallenge}
    >
      <MaturityModel
        interactiveTableSteps={props.children}
        interactiveTable={{
          colTitle: props.colTitle,
          rowTitle: props.rowTitle,
          colHeader,
          rowHeader,
        }}
        tableCells={tableCells}
        tableControls={{
          title: props.controlsTitle || "Key",
          options: props.controlsOptions || [],
          clearButton: props.clearButtonText || "Clear Model",
          analytics: { dld: props.dld, dlt: props.dlt },
          clearModal: {
            headline: props.clearModalConfig?.headline || "Clear Model",
            description:
              props.clearModalConfig?.description ||
              "This will reset all selections.",
            backButtonText: props.clearModalConfig?.backButtonText || "Cancel",
            backButtonAnalytics: props.clearModalConfig?.backButtonAnalytics,
            startNewButtonText:
              props.clearModalConfig?.startNewButtonText || "Clear",
            startNewButtonAnalytics:
              props.clearModalConfig?.startNewButtonAnalytics,
          },
        }}
        hasSelectedItems={hasSelectedItems}
        selectedChallenge={localChallenge}
        resetCounter={resetCounter}
        onSelectionValidChange={handleSelectionValidChange}
        onChallengeChange={handleSetSelectedChallenge}
        onClearModel={handleClearModel}
      />
    </MaturityModelProvider>
  );
};
